import { useCouponManualInput } from '@cart-services/hooks/useCouponManualInput'
import { useFpCampaignCartCalculate } from '@cart-services/hooks/useFpCampaignCartCalculate'
import { useFpCampaignCoupons } from '@cart-services/hooks/useFpCampaignCoupons'
import { useFpCampaignCouponService } from '@cart-services/hooks/useFpCampaignCouponService'
import { type GetCouponManualInputExceptionMessageReturn } from '@cart-services/utils/errorHandlers'
import { CouponManualInput } from '@cart-ui/CouponManualInput/CouponManualInput'
import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCalculateCampaignCoupons } from '@hooks-api/fortune/useApiCalculateCampaignCoupons'
import { useApiCampaignGetCouponIdByCode } from '@hooks-api/fortune/useApiCampaignGetCouponIdByCode'
import { UsageMethod } from '@hooks-api/types/dataModel/coupon'
import { type InputProps } from '@pets-ui/Input/Input'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { type FormEvent, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface DialogCouponManualInputProps {
  campaignId: number
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignCode: string
}

export const DialogCouponManualInput = (props: DialogCouponManualInputProps) => {
  const { campaignId, projectCode, countryCode, campaignCode } = props
  const { calculateValue } = useFpCampaignCartCalculate(campaignId, campaignCode)
  // const [, setCartValues] = useFpCampaignCartStore(campaignId)
  const { addManualInputCouponIds, removeManualInputCouponIds } = useFpCampaignCouponService(campaignId, countryCode, campaignCode)
  const { trigger: getCouponIdByCodeTrigger, isMutating: isMutatingGetCouponIdByCode } = useApiCampaignGetCouponIdByCode()
  const { trigger: calculateCouponsTrigger, isMutating: isMutatingCalculateCampaignCoupons } = useApiCalculateCampaignCoupons({
    campaign_code: campaignCode,
  })
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { availableMergedCoupons } = useFpCampaignCoupons(campaignId, countryCode, campaignCode)
  const { t } = useTranslation()
  const { sendApplyCouponCodeEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const getCouponIdByCode = useCallback((couponCode: string) => getCouponIdByCodeTrigger({
    project_code: projectCode,
    campaign_code: campaignCode,
    coupon_code: couponCode,
  }), [campaignCode, getCouponIdByCodeTrigger, projectCode])

  const calculateCoupons = useCallback((manualInputCouponIds: number[]) => calculateCouponsTrigger({
    ...calculateValue,
    manual_input_coupon_ids: manualInputCouponIds,
  }), [calculateValue, calculateCouponsTrigger])

  const onApplyCoupon = useCallback((manualInputCouponIds: number[]) => {
    addManualInputCouponIds(manualInputCouponIds)
  }, [addManualInputCouponIds])

  const onApplyCouponSuccess = useCallback((message: string) => {
    // const manualInputCoupon = calculateResponse.data.applied_coupons.find(appliedCoupon => coupon.coupon_id === appliedCoupon.coupon_id)
    // NOTE: 在 2503 實作，手動輸入的優惠券，含有 giveaways 的優惠券，但目前後端沒有回傳 giveaways，所以先註解
    // if (manualInputCoupon?.criterions.some(criterion => criterion.giveaways?.length)) {
    //   setCartValues((prev) => ({
    //     ...prev,
    //     isGiveawayDispatch: true,
    //   }))
    // }
    openSnackbar({
      message,
      severity: 'success',
    })
  }, [openSnackbar])

  const onApplyCouponSuccessNotQualified = useCallback((message: string) => {
    openSnackbar({
      message,
      severity: 'warning',
    })
  }, [openSnackbar])

  const onApplyCouponError = useCallback((message: string, manualInputCouponIds: number[]) => {
    removeManualInputCouponIds(manualInputCouponIds)
    openSnackbar({
      message,
      severity: 'error',
    })
  }, [openSnackbar, removeManualInputCouponIds])

  const onGetCouponError = useCallback((exceptionMessage: GetCouponManualInputExceptionMessageReturn) => {
    openSnackbar({
      message: t(exceptionMessage.message, { ...exceptionMessage.value, defaultValue: exceptionMessage.message }),
      severity: 'error',
    })
  }, [openSnackbar, t])

  /**
   * 不直接從 calculateValue.manual_input_coupon_ids 取得，
   * 因為 calculateValue.manual_input_coupon_ids 可能包含已過期的優惠券
   */
  const currentManualInputCouponIds = useMemo(() =>
    availableMergedCoupons?.filter(coupon => coupon.usage_method === UsageMethod.MANUAL).map(coupon => coupon.coupon_id),
  [availableMergedCoupons])

  const {
    couponCode,
    setCouponCode,
    handleSubmit,
  } = useCouponManualInput({
    getCouponIdByCode,
    calculateCoupons,
    onApplyCoupon,
    onApplyCouponSuccess,
    onApplyCouponSuccessNotQualified,
    onApplyCouponError,
    onGetCouponError,
    currentManualInputCouponIds,
  })

  const handleChange: InputProps['onChange'] = useCallbackEvent((event) => {
    setCouponCode(event.target.value)
  })

  const handleSubmitManualCoupon = useCallbackEvent((e: FormEvent<HTMLFormElement>) => {
    handleSubmit(e)

    const appliedCoupon = availableMergedCoupons?.find(coupon =>
      coupon.usage_method === UsageMethod.MANUAL && coupon.coupon_code === couponCode,
    )

    if (appliedCoupon) {
      sendApplyCouponCodeEvent({
        couponId: String(appliedCoupon.coupon_id),
        couponTitle: appliedCoupon.display_name,
        // NOTE: 取最後一個 applied_status 為 applied 的 criterion 的 display_name 作為門檻（代表他有達到那個門檻）
        couponThreshold: appliedCoupon.criterions?.filter(criterion => criterion.applied_status === 'applied').pop()?.display_name || '',
      })
    }
  })

  return (
    <CouponManualInput
      onSubmit={handleSubmitManualCoupon}
      value={couponCode}
      onChange={handleChange}
      disabled={isMutatingGetCouponIdByCode || isMutatingCalculateCampaignCoupons}
      placeholder="請輸入優惠碼"
      submitText="抵用"
    />
  )
}
